import React from 'react';
import ShipmentStatusBadge from './ShipmentStatusBadge';

const inputStyle = {
  width: '100%',
  padding: '6px 10px',
  fontSize: '12px',
  border: '1px solid var(--border)',
  borderRadius: '6px',
  backgroundColor: 'var(--card-bg)',
  color: 'var(--text)',
  outline: 'none'
};

const buttonStyle = {
  padding: '6px 12px',
  fontSize: '12px',
  fontWeight: '500',
  display: 'flex',
  alignItems: 'center',
  gap: '0.35rem'
};

const STATS_CONFIG = [
  { key: 'total', label: 'Wszystkich', color: 'var(--primary)' },
  { key: 'in_transit', label: 'W drodze', color: 'var(--info)' },
  { key: 'delivered', label: 'Dostarczonych', color: 'var(--success)' },
  { key: 'cancelled', label: 'Anulowanych', color: 'var(--danger)' }
];

const ShipmentsList = ({
  stats,
  paginatedShipments,
  filteredShipments,
  shipmentsLoading,
  filterStatus, setFilterStatus,
  searchTerm, setSearchTerm,
  currentPage, setCurrentPage,
  perPage, setPerPage,
  totalPages, startIndex,
  selectedForPickup,
  onToggleSelectForPickup,
  onToggleSelectAll,
  onShowPickupModal,
  onRefresh,
  onSyncTracking,
  syncing,
  onShowDetails,
  onRefreshStatus,
  onDownloadLabel,
  onCancelShipment,
  canOrderPickups,
  canDownloadLabels,
  canEditShipments,
}) => {
  const handleClearFilters = () => { setSearchTerm(''); setFilterStatus(''); };

  return (
    <div>
      {/* Statystyki */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {STATS_CONFIG.map(stat => (
          <div key={stat.key} style={{
            backgroundColor: 'var(--card-bg)',
            padding: '0.5rem 0.75rem',
            borderRadius: '4px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
            borderLeft: `3px solid ${stat.color}`,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{stat.label}:</span>
            <span style={{ fontSize: '14px', fontWeight: '700', color: stat.color }}>{stats[stat.key]}</span>
          </div>
        ))}
      </div>

      {/* Filtry */}
      <div style={{
        backgroundColor: 'var(--bg)',
        padding: '0.75rem',
        borderRadius: '8px',
        marginBottom: '1rem',
        display: 'flex',
        gap: '0.5rem',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <input
          type="text"
          placeholder="Szukaj (numer, klient)..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          style={{ ...inputStyle, width: '200px' }}
        />
        <select
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
          style={{ ...inputStyle, width: '150px' }}
        >
          <option value="">Wszystkie statusy</option>
          <option value="in_transit">W drodze</option>
          <option value="ready">Gotowe do odbioru</option>
          <option value="delivered">Dostarczone</option>
          <option value="problem">Problemy</option>
          <option value="cancelled">Anulowane</option>
        </select>
        {(searchTerm || filterStatus) && (
          <button onClick={handleClearFilters} style={{ ...buttonStyle, border: '1px solid var(--text-muted)', color: 'var(--text-muted)' }}>
            Wyczysc filtry
          </button>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
          {selectedForPickup.length > 0 && canOrderPickups && (
            <button
              onClick={onShowPickupModal}
              style={{ ...buttonStyle, border: '1px solid var(--orange)', color: 'var(--orange)', fontWeight: '600' }}
            >
              Zamow odbior ({selectedForPickup.length})
            </button>
          )}
          <button onClick={onRefresh} style={{ ...buttonStyle, border: '1px solid var(--info)', color: 'var(--info)' }}>
            Odswiez
          </button>
          <button
            onClick={onSyncTracking}
            disabled={syncing}
            style={{ ...buttonStyle, border: `1px solid ${syncing ? 'var(--text-muted)' : 'var(--purple)'}`, color: syncing ? 'var(--text-muted)' : 'var(--purple)' }}
            title="Pobierz numery sledzenia dla przesylek ktore ich nie maja"
          >
            {syncing ? 'Synchronizuje...' : 'Pobierz nr sledzenia'}
          </button>
        </div>
      </div>

      {/* Tabela */}
      {shipmentsLoading ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Ladowanie przesylek...</div>
      ) : filteredShipments.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Brak przesylek</div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--bg)' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '2px solid var(--border)', width: '35px' }}>
                    <input
                      type="checkbox"
                      onChange={onToggleSelectAll}
                      checked={
                        paginatedShipments.filter(s => ['created', 'confirmed', 'offers_prepared'].includes(s.status)).length > 0 &&
                        paginatedShipments.filter(s => ['created', 'confirmed', 'offers_prepared'].includes(s.status))
                          .every(s => selectedForPickup.some(sel => sel.id === s.id))
                      }
                      style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                    />
                  </th>
                  <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '2px solid var(--border)', width: '40px' }}>LP</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Numer sledzenia</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Odbiorca</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Usluga</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Odbior</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Status</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Data</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {paginatedShipments.map((shipment, idx) => {
                  const canPickup = ['created', 'confirmed', 'offers_prepared'].includes(shipment.status);
                  const isSelected = selectedForPickup.some(s => s.id === shipment.id);
                  return (
                    <tr key={shipment.id} style={{ backgroundColor: isSelected ? 'var(--orange-light)' : (idx % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)') }}>
                      <td style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '1px solid var(--border)' }}>
                        {canPickup ? (
                          <input type="checkbox" checked={isSelected} onChange={() => onToggleSelectForPickup(shipment)}
                            style={{ width: '14px', height: '14px', cursor: 'pointer' }} />
                        ) : (
                          <span style={{ color: 'var(--border)' }}>-</span>
                        )}
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)' }}>
                        {startIndex + idx + 1}
                      </td>
                      <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)' }}>
                        <strong style={{ fontFamily: 'monospace' }}>{shipment.tracking_number || '-'}</strong>
                      </td>
                      <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)' }}>
                        <div>{shipment.receiver_name}</div>
                        {shipment.target_point && (
                          <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>{shipment.target_point}</div>
                        )}
                      </td>
                      <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)', fontSize: '10px' }}>
                        {shipment.service === 'inpost_locker_standard' && 'Paczkomat->Paczkomat'}
                        {shipment.service === 'inpost_locker_to_address' && 'Paczkomat->Kurier'}
                        {shipment.service === 'inpost_courier_locker' && 'Kurier->Paczkomat'}
                        {shipment.service === 'inpost_courier_standard' && 'Kurier->Adres'}
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '1px solid var(--border)', fontSize: '9px' }}>
                        {shipment.pickup_status === 'paczkomat' && <span style={{ color: 'var(--text-muted)' }}>Nadaj w paczkomacie</span>}
                        {shipment.pickup_status === 'zlecony' && <span style={{ color: 'var(--success)', fontWeight: '600' }}>Zlecony</span>}
                        {shipment.pickup_status === 'odebrany' && <span style={{ color: 'var(--info)' }}>Odebrany</span>}
                        {shipment.pickup_status === 'niezlecony' && (
                          <span style={{ color: 'var(--danger)', fontWeight: '600', backgroundColor: 'var(--warning-light)', padding: '2px 6px', borderRadius: '4px', display: 'inline-block' }}>
                            Zloz zlecenie odbioru!
                          </span>
                        )}
                        {shipment.pickup_status === 'nie_dotyczy' && <span style={{ color: 'var(--text-muted)' }}>-</span>}
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '1px solid var(--border)' }}>
                        <ShipmentStatusBadge status={shipment.status} size="small" />
                      </td>
                      <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)', fontSize: '10px', color: 'var(--text-muted)' }}>
                        {new Date(shipment.created_at).toLocaleDateString('pl-PL')}
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '1px solid var(--border)' }}>
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                          <button onClick={() => onShowDetails(shipment)} title="Szczegoly"
                            style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--info)', color: 'var(--info)' }}>
                            Details
                          </button>
                          <button onClick={() => onRefreshStatus(shipment)} title="Odswiez status"
                            style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--info)', color: 'var(--info)' }}>
                            Refresh
                          </button>
                          {canDownloadLabels && (
                            <button onClick={() => onDownloadLabel(shipment)} title="Pobierz etykiete"
                              style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--success)', color: 'var(--success)' }}>
                              Label
                            </button>
                          )}
                          {canEditShipments && ['created', 'offers_prepared'].includes(shipment.status) && (
                            <button onClick={() => onCancelShipment(shipment)} title="Anuluj przesylke"
                              style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--danger)', color: 'var(--danger)' }}>
                              Cancel
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Paginacja */}
          <div style={{
            padding: '0.75rem',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '0.5rem',
            flexWrap: 'wrap'
          }}>
            <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}
              style={{ ...inputStyle, width: '70px' }}>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}
              style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--border)', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', backgroundColor: 'var(--card-bg)' }}>
              «
            </button>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--border)', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', backgroundColor: 'var(--card-bg)' }}>
              &larr;
            </button>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Strona {currentPage} z {totalPages || 1}</span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}
              style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--border)', cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer', backgroundColor: 'var(--card-bg)' }}>
              &rarr;
            </button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage >= totalPages}
              style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--border)', cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer', backgroundColor: 'var(--card-bg)' }}>
              »
            </button>
            <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
              ({startIndex + 1}-{Math.min(startIndex + perPage, filteredShipments.length)} z {filteredShipments.length})
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default ShipmentsList;
